import type { FC } from "react"
import React from "react"
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  HStack,
  Icon,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react"
import type { BigNumber } from "ethers"

import { getWeightLabel } from "common/helpers/bignumber"
import { ROUTES } from "common/constants/routes"
import WarningTriangleIcon from "ui/components/icons/WarningTriangleIcon"
import type { Maybe } from "query/graphql"

type ProposalThresholdWarningModalProps = {
  isOpen: boolean
  onClose: () => void
  governanceSlug: string
  proposalThreshold?: Maybe<string>
  decimals: number
  delegatedVotingPower: BigNumber
}
const ProposalThresholdWarningModal: FC<ProposalThresholdWarningModalProps> = ({
  isOpen,
  onClose,
  governanceSlug,
  delegatedVotingPower,
  proposalThreshold,
  decimals,
}) => {
  const cancelRef = React.useRef(null)

  const proposalThresholdLabel = proposalThreshold
    ? getWeightLabel(proposalThreshold, decimals)
    : ""
  const delegatedVotingPowerLabel = getWeightLabel(
    delegatedVotingPower,
    decimals,
  )

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader
            borderBottomColor="gray.200"
            borderBottomWidth="1px"
            fontSize="lg"
            pt={5}
          >
            <HStack>
              <Icon as={WarningTriangleIcon} color="yellow.500" h={6} w={6} />
              <Text>Not enough voting power</Text>
            </HStack>
          </AlertDialogHeader>
          <AlertDialogCloseButton mt={2} />
          <AlertDialogBody
            borderBottomColor="gray.200"
            borderBottomWidth="1px"
            py={4}
          >
            <Stack spacing={4}>
              <Text fontSize="sm" fontWeight="bold">
                Proposal Threshold:{" "}
                <Text as="span" fontWeight="normal">
                  {proposalThresholdLabel}
                </Text>
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                Your Voting Power:{" "}
                <Text as="span" fontWeight="normal">
                  {delegatedVotingPowerLabel}
                </Text>
              </Text>
              <Text
                fontSize="md"
                fontWeight="regular"
              >{`You won't be able to submit this proposal onchain. You can still save your proposal as a draft and share it with a sponsor.`}</Text>
              <Flex>
                <Link
                  isExternal
                  href={ROUTES.governance.delegates(governanceSlug)}
                >
                  <Text color="purple.500">Find a sponsor</Text>
                </Link>
              </Flex>
            </Stack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              variant="secondary"
              width={{ base: "100%", sm: "auto" }}
              onClick={onClose}
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default ProposalThresholdWarningModal
